<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick, inject } from 'vue';

const props = defineProps({
  targetLeft: Boolean,
  text: {
    type: String,
    default: ''
  },
  theme: {
    type: String,
    default: 'primary',
    validator: (value) => {
      return ['primary', 'secondary'].includes(value);
    },
  },
  xPosition: {
    type: String,
    default: 'right',
    validator: (value) => {
      return ['right', 'left', 'center'].includes(value);
    },
  },
  initial: {
    type: String,
    default: 'bottom',
    validator: (value) => {
      return ['bottom', 'top', 'left', 'right'].includes(value);
    },
  },
  rect: {
    type: DOMRect,
    required: true
  },
  custom: Boolean,
  persistent: Boolean,
  distance: {
    type: Number,
    default: 4
  }
});
const emit = defineEmits(['close']);
const { theme, xPosition, persistent, distance } = props;
const BACKGROUND_COLOR = theme === 'primary' ? '#342F35' : '#F5EBF3';
const BORDER_RADIUS = theme === 'primary' ? '4px' : '12px';
const MAX_WIDTH = theme === 'primary' ? '100%' : '312px';
const BOX_SHADOW = theme === 'primary' ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.3)';
// provide/inject to resolve prop-drilling
const initial = inject('initial', props.initial);

const tooltip = ref(null);
const isTooltipOpen = ref(false);
const tooltipPosition = ref(initial);
const animationClass = ref(`scale-in-${tooltipPosition.value}-${xPosition}`);
const animationTimeout = ref(null);

const removeTooltip = () => {
  if (isTooltipOpen.value) {
    animationClass.value = `scale-out-${tooltipPosition.value}-${xPosition}`;
  }
};

const handleTooltipMouseOver = () => {
  if (!persistent) {
    if (animationTimeout.value) {
      clearTimeout(animationTimeout.value);
    }
  }
};

const handleTooltipMouseLeave = () => {
  if (!persistent) {
    animationTimeout.value = setTimeout(() => {
      removeTooltip();
    }, 50);
  }
};

const handleTooltipAnimationEnd = () => {
  if (animationClass.value.startsWith('scale-out')) {
    isTooltipOpen.value = false;
    emit('close');
  }
};

watch(isTooltipOpen, (newVal) => {
  const { rect } = props;
  if (tooltip.value && rect && newVal) {
    const tooltipHeight = tooltip.value.offsetHeight;
    const tooltipWidth = tooltip.value.offsetWidth;
    tooltip.value.style.left = `${rect.right - tooltipWidth}px`;
    if (xPosition === 'left') {
      tooltip.value.style.left = `${rect.left}px`;
    } else if (xPosition === 'center') {
      tooltip.value.style.left = `${rect.left + (rect.width - tooltipWidth) / 2}px`;
    }
    // Insure the tooltip is visible within the viewport
    // When there is not enough space to the bottom/top, show the tooltip on the opposite side
    const topPosition = `${rect.top + window.scrollY - tooltipHeight - distance}px`;
    const bottomPosition = `${rect.bottom + window.scrollY + distance}px`;
    const bottomDistance = window.innerHeight - rect.bottom - tooltipHeight - distance;
    const topDistance = rect.top - tooltipHeight - distance;
    if (initial === 'bottom') {
      if (bottomDistance < 0 && topDistance > bottomDistance) {
        tooltipPosition.value = 'bottom';
        tooltip.value.style.top = topPosition;
      } else {
        tooltipPosition.value = 'top';
        tooltip.value.style.top = bottomPosition;
      }
    } else if (initial === 'top') {
      if (topDistance < 0 && topDistance < bottomDistance) {
        tooltipPosition.value = 'top';
        tooltip.value.style.top = bottomPosition;
      } else {
        tooltipPosition.value = 'bottom';
        tooltip.value.style.top = topPosition;
      }
    } else {
      const leftPosition = `${rect.left + window.scrollX - tooltipWidth - distance}px`;
      const rightPosition = `${rect.right + window.scrollX + distance}px`;
      const rightDistance = window.innerWidth - rect.right - tooltipWidth - distance;
      const leftDistance = rect.left - tooltipWidth - distance;
      tooltip.value.style.top = `${rect.top + window.scrollY + (rect.height - tooltipHeight) / 2}px`;
      if (initial === 'left') {
        if (leftDistance < 0 && leftDistance < rightDistance) {
          tooltipPosition.value = 'left';
          tooltip.value.style.left = rightPosition;
        } else {
          tooltipPosition.value = 'right';
          tooltip.value.style.left = leftPosition;
        }
      } else if (initial === 'right') {
        const leftDistance = rect.left - tooltipWidth - distance;
        if (rightDistance < 0 && leftDistance > rightDistance) {
          tooltipPosition.value = 'right';
          tooltip.value.style.left = leftPosition;
        } else {
          tooltipPosition.value = 'left';
          tooltip.value.style.left = rightPosition;
        }
      }
    }
    animationClass.value = `scale-in-${tooltipPosition.value}-${xPosition}`;
  }
}, {
  // Make sure the watcher is triggered after the tooltip is rendered
  // But not yet painted to avoid flickering
  flush: 'post'
});

watch(() => props.targetLeft, async (newVal) => {
  if (newVal) {
    if (!persistent) {
      animationTimeout.value = setTimeout(() => {
        animationClass.value = `scale-out-${tooltipPosition.value}-${xPosition}`;
      }, 50);
    } else {
      animationClass.value = `scale-out-${tooltipPosition.value}-${xPosition}`;
    }
  } else {
    if (animationTimeout.value) {
      clearTimeout(animationTimeout.value);
      animationClass.value = `scale-in-${tooltipPosition.value}-${xPosition}`;
    }
  }
});

defineExpose({
  tooltip,
});

onMounted(async () => {
  isTooltipOpen.value = true;
  window.addEventListener('scroll', removeTooltip, true);
  await nextTick();
  tooltip.value.focus();
});

onUnmounted(() => {
  window.removeEventListener('scroll', removeTooltip, true);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isTooltipOpen"
      ref="tooltip"
      tabindex="-1"
      role="tooltip"
      :class="`pge-tooltip ${animationClass}`"
      :style="{
        '--MAX_WIDTH': MAX_WIDTH,
        '--BORDER_RADIUS': BORDER_RADIUS,
        '--BACKGROUND_COLOR': BACKGROUND_COLOR,
        '--BOX_SHADOW': BOX_SHADOW,
      }"
      @mouseenter="handleTooltipMouseOver"
      @mouseleave="handleTooltipMouseLeave"
      @keyup.esc="removeTooltip"
      @animationend="handleTooltipAnimationEnd"
    >
      <slot v-if="custom" />
      <span v-else>{{ text }}</span>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.pge-tooltip {
  position: absolute;
  max-width: var(--MAX_WIDTH);
  width: max-content;
  padding: 4px 8px;
  border-radius: var(--BORDER_RADIUS);
  background-color: var(--BACKGROUND_COLOR);
  color: #F7EEF6;
  overflow-wrap: anywhere;
  font-size: 12px;
  line-height: 16px;
  box-shadow: var(--BOX_SHADOW);
  z-index: 99999;

  &:focus-within {
    outline: none;
  }
}
</style>
